import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Flame, Activity, Trophy } from "lucide-react";
import { playPulseChime } from "../engine/sound";
import { streakStats } from "../../lib/orank";

const GOLD = "#D4AF37", RED = "#C41E24";
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HEX = "50,4 91,27 91,73 50,96 9,73 9,27";

const iso = (d) => d.toISOString().slice(0, 10);
const shift = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// Mon -> Sun of the current week as ISO strings.
const weekDates = (today) => {
  const dow = (today.getDay() + 6) % 7;
  const mon = shift(today, -dow);
  return DAYS.map((_, i) => iso(shift(mon, i)));
};

const Hex = ({ state, label, accent, onClick, flip }) => {
  const filled = state === "done";
  const fill = filled ? accent : state === "missed" ? "rgba(196,30,36,0.18)" : "transparent";
  const stroke = filled ? accent : state === "missed" ? `${RED}aa` : state === "today" ? accent : "rgba(255,255,255,0.14)";
  const glow = filled ? `drop-shadow(0 0 10px ${accent}) drop-shadow(0 0 22px ${accent}88)` : state === "missed" ? `drop-shadow(0 0 8px ${RED}55)` : "none";
  return (
    <button type="button" data-testid={`pulse-hex-${label.toLowerCase()}`} data-state={state} onClick={onClick} disabled={state !== "today"} aria-label={`${label}: ${state}`}
      className={`relative flex flex-col items-center gap-1.5 ${state === "today" ? "cursor-pointer" : "cursor-default"}`}>
      <motion.svg viewBox="0 0 100 100" className="w-9 h-9 sm:w-12 sm:h-12" style={{ filter: glow, overflow: "visible" }}
        animate={flip ? { scale: [1, 1.35, 1], rotate: [0, 12, 0] } : state === "today" ? { scale: [1, 1.06, 1] } : {}}
        transition={flip ? { duration: 0.7, ease: "easeOut" } : { duration: 2.2, repeat: Infinity, ease: "easeInOut" }}>
        <polygon points={HEX} fill={fill} stroke={stroke} strokeWidth={state === "today" ? 4 : 3} strokeLinejoin="round" strokeDasharray={state === "future" ? "6 6" : undefined} />
        {state === "today" && <motion.polygon points={HEX} fill="none" stroke={accent} strokeWidth={2} animate={{ opacity: [0.2, 0.9, 0.2], scale: [1, 1.18, 1] }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} style={{ transformOrigin: "50% 50%" }} />}
        {filled && <polyline points="32,52 45,64 70,38" fill="none" stroke="#0a0a0a" strokeWidth={9} strokeLinecap="round" strokeLinejoin="round" />}
        {state === "missed" && <g stroke={`${RED}cc`} strokeWidth={7} strokeLinecap="round"><line x1="36" y1="36" x2="64" y2="64" /><line x1="64" y1="36" x2="36" y2="64" /></g>}
      </motion.svg>
      <span className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.2em]" style={{ color: filled || state === "today" ? accent : "rgba(255,255,255,0.4)" }}>{label}</span>
    </button>
  );
};

// Technical Pulse — 7-hex weekly rep tracker synced live to journal entries.
export default function TechnicalPulse({ entries, isElite, playerId }) {
  const navigate = useNavigate();
  const accent = isElite ? GOLD : RED;
  const todayStr = iso(new Date());
  const days = useMemo(() => new Set((entries || []).map((e) => e.date).filter(Boolean)), [entries]);
  const week = useMemo(() => weekDates(new Date()), [todayStr]); // eslint-disable-line react-hooks/exhaustive-deps
  const { cur, best } = useMemo(() => streakStats(days, todayStr), [days, todayStr]);
  const doneThisWeek = week.filter((d) => days.has(d)).length;

  // Success flip + chime the first time today's hex is seen lit (persisted per player so it fires once per day).
  const [flip, setFlip] = useState(false);
  const seenKey = playerId ? `nexto_pulse_seen_${playerId}` : null;
  const firedRef = useRef(false);
  useEffect(() => {
    if (!seenKey || firedRef.current || !days.has(todayStr)) return;
    if (localStorage.getItem(seenKey) === todayStr) return;
    firedRef.current = true;
    localStorage.setItem(seenKey, todayStr);
    setFlip(true);
    playPulseChime();
    const t = setTimeout(() => setFlip(false), 900);
    return () => clearTimeout(t);
  }, [days, todayStr, seenKey]);

  const stateOf = (d) => (days.has(d) ? "done" : d === todayStr ? "today" : d < todayStr ? "missed" : "future");

  return (
    <motion.section data-testid="technical-pulse" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
      className={`relative rounded-xl border backdrop-blur-xl overflow-hidden grain ${isElite ? "elite-carbon-bg" : "bg-[#121212]"}`}
      style={{ borderColor: `${accent}44`, boxShadow: `0 20px 60px rgba(0,0,0,.5), 0 0 40px ${accent}14, inset 0 1px 0 ${accent}55` }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(520px 160px at 15% 0%, ${accent}22, transparent 70%)` }} />
      <div className="relative flex flex-col gap-5 px-5 sm:px-7 py-5 h-full justify-between">
        <div className="min-w-0">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em] whitespace-nowrap" style={{ color: accent }}><Activity size={12} /> Technical Pulse</div>
            <div data-testid="pulse-week-count" className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 whitespace-nowrap">This week · {doneThisWeek}/7</div>
          </div>
          <div data-testid="pulse-hex-row" className="flex items-start justify-between gap-1 sm:gap-2">
            {week.map((d, i) => <Hex key={d} label={DAYS[i]} state={stateOf(d)} accent={accent} flip={flip && d === todayStr} onClick={() => navigate("/read/journal")} />)}
          </div>
        </div>
        <div className="flex items-center gap-4 pt-4 border-t" style={{ borderColor: `${accent}33` }}>
          <div className="flex items-center gap-3">
            <Flame size={30} className={cur > 0 ? "animate-pulse" : ""} style={{ color: cur > 0 ? accent : "rgba(255,255,255,0.25)", filter: cur > 0 ? `drop-shadow(0 0 14px ${accent})` : "none" }} fill={cur > 0 ? accent : "none"} />
            <div className="leading-none">
              <div className="flex items-baseline gap-1.5 overflow-hidden">
                <span data-testid="pulse-streak-count" className="inline-flex"><AnimatePresence mode="popLayout" initial={false}>
                  <motion.span key={cur} initial={{ y: 28, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -28, opacity: 0 }} transition={{ type: "spring", stiffness: 380, damping: 26 }}
                    className="font-cover text-4xl sm:text-5xl" style={{ color: cur > 0 ? accent : "rgba(255,255,255,0.5)", textShadow: cur > 0 ? `0 0 24px ${accent}88` : "none" }}>{cur}</motion.span>
                </AnimatePresence></span>
                <span className="font-headings text-sm uppercase tracking-wide text-white/80">Day Streak</span>
              </div>
              <div data-testid="pulse-best-streak" className="mt-1.5 flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45"><Trophy size={10} /> Best · {best} days</div>
            </div>
          </div>
          {!days.has(todayStr) && (
            <button data-testid="pulse-log-btn" onClick={() => navigate("/read/journal")} className="ml-auto text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm border transition-colors hover:text-black whitespace-nowrap" style={{ borderColor: `${accent}66`, color: accent }}
              onMouseEnter={(e) => { e.currentTarget.style.background = accent; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>Log Today's Reps</button>
          )}
        </div>
      </div>
    </motion.section>
  );
}
